import { useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Minus, Plus, Package, ShoppingCart, Sparkles, Zap, Store } from "lucide-react";
import { toast } from "sonner";
import clsx from "clsx";

import { get } from "@/lib/api-client";
import { formatNumber } from "@/lib/format";
import { Card, CardBody } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { Skeleton } from "@/components/ui/Skeleton";
import { useUIStore } from "@/store/ui-store";
import { useAuthStore } from "@/store/auth-store";
import type { Product } from "@/types/catalog";

// Custom Formatter for Tk
function formatPriceTk(amount: string | number) {
  const n = typeof amount === "string" ? Number(amount) : amount;
  const numStr = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number.isFinite(n) ? n : 0);
  return `Tk ${numStr}`;
}

export function ProductDetailPage() {
  const { productCode } = useParams();
  const nav = useNavigate();
  
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [selectedOptions, setSelectedOptions] = useState<Record<string, string>>({});
  const [qty, setQty] = useState(1);
  const addToCart = useUIStore((s) => s.addToCart);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  const { data, isLoading } = useQuery({
    queryKey: ["product", productCode],
    queryFn: () => get<Product>(`/catalog/products/${productCode}`),
    enabled: !!productCode,
  });

  // Dynamic Variant Resolution logic
  const availableOptions = useMemo(() => {
    if (!data) return {};
    const opts: Record<string, Set<string>> = {};
    data.variants.forEach(v => {
      Object.entries(v.attributes).forEach(([key, val]) => {
        if (!opts[key]) opts[key] = new Set();
        opts[key].add(val);
      });
    });
    return opts;
  }, [data]);

  const variant = useMemo(() => {
    if (!data) return null;
    if (data.variants.length === 1 && Object.keys(data.variants[0].attributes).length === 0) {
      return data.variants[0];
    }
    return data.variants.find(v => {
      return Object.entries(selectedOptions).every(([k, val]) => v.attributes[k] === val);
    }) || null;
  }, [data, selectedOptions]);

  if (isLoading || !data) {
    return (
      <div className="max-w-6xl mx-auto space-y-6 p-4 sm:p-6 lg:p-8">
        <Skeleton className="h-10 w-24" />
        <div className="grid gap-8 lg:grid-cols-2">
          <Skeleton className="aspect-[4/3] rounded-2xl" />
          <div className="space-y-4">
            <Skeleton className="h-10 w-3/4" />
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-14 w-full mt-8" />
          </div>
        </div>
      </div>
    );
  }

  // 🛠️ SMART PRICE LOGIC
  const price1 = Number(variant ? variant.price : data.base_price) || 0;
  const price2 = Number(variant?.compare_at_price ?? data.compare_at_price) || 0;

  let sellingPrice = price1;
  let crossedOutPrice = null;

  if (price2 > 0) {
    if (price2 > price1) {
      crossedOutPrice = price2;
      sellingPrice = price1;
    } else {
      crossedOutPrice = price1;
      sellingPrice = price2;
    }
  }

  const priceText = formatPriceTk(sellingPrice);
  const comparePrice = crossedOutPrice ? formatPriceTk(crossedOutPrice) : null;
  const points = variant?.effective_qualification_points ?? data.qualification_point_value;

  const handleAction = (isBuyNow: boolean) => {
    if (!isAuthenticated) {
      toast.info("Please sign in or create an account to purchase this item.");
      nav("/login");
      return;
    }

    if (data.variants.length > 0 && !variant) {
      toast.error("Please select all options before proceeding.");
      return;
    }
    if (!variant) return toast.error("Product unavailable.");
    
    addToCart(variant.id, qty);
    
    if (isBuyNow) {
      nav(`/dashboard/checkout`); 
    } else {
      toast.success(`${qty} × ${data.name} added to cart`);
    }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6 p-4 sm:p-6 lg:p-8 animate-fade-in">
      <button onClick={() => nav(-1)} className="text-sm font-semibold text-surface-500 hover:text-surface-900 transition-colors">
        ← Back
      </button>

      <div className="grid gap-8 lg:gap-12 lg:grid-cols-2">
        {/* 📸 Advanced Image Gallery */}
        <div className="space-y-4">
          
          {/* 🛠️ FIXED: Image wrapper now has NO PADDING and uses object-cover to perfectly fill the box */}
          <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-white border border-surface-200 shadow-sm flex items-center justify-center">
            {data.images.length > 0 && data.images[activeImageIndex] ? (
              <img src={data.images[activeImageIndex].image} className="w-full h-full object-cover" alt={data.name} />
            ) : (
              <div className="h-full w-full flex items-center justify-center text-surface-300 bg-surface-50">
                <Package className="h-24 w-24 opacity-30" />
              </div>
            )}
          </div>
          
          {data.images.length > 1 && (
            <div className="flex gap-3 overflow-x-auto pb-2">
              {data.images.map((img, idx) => (
                <button 
                  key={img.id} onClick={() => setActiveImageIndex(idx)}
                  className={clsx("w-20 h-20 shrink-0 rounded-xl overflow-hidden border-2 transition-all bg-white", activeImageIndex === idx ? "border-brand-500 shadow-md scale-105" : "border-surface-200 opacity-70 hover:opacity-100 hover:border-brand-300")}
                >
                  <img src={img.image} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* 🛒 Product Info & Actions */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Badge tone="info">{data.category.name}</Badge>
            {data.brand && <Badge tone="neutral" className="uppercase font-bold tracking-wider">{data.brand}</Badge>}
            {data.vendor_shop_name && (
              <Badge tone="warning" className="flex gap-1 items-center font-bold">
                <Store className="w-3 h-3"/> {data.vendor_shop_name}
              </Badge>
            )}
          </div>
          
          <h1 className="text-3xl lg:text-4xl font-extrabold text-surface-900 tracking-tight leading-tight">{data.name}</h1>
          <p className="mt-4 text-lg text-surface-600 leading-relaxed">{data.short_description || "Premium quality product."}</p>

          {/* 🛠️ FIXED: Price and PV beautifully aligned in a single row with similar font sizes */}
          <div className="mt-6 flex flex-wrap items-center gap-4 sm:gap-6 bg-brand-50/50 border border-brand-100 p-4 sm:p-5 rounded-2xl shadow-sm">
            
            {/* Price Section */}
            <div className="flex flex-col justify-center">
              {comparePrice && (
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm sm:text-base text-surface-400 line-through font-bold leading-none">
                    {comparePrice}
                  </span>
                  <span className="bg-danger-100 text-danger-700 text-[10px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider">
                    Save {Math.round(((crossedOutPrice! - sellingPrice) / crossedOutPrice!) * 100)}%
                  </span>
                </div>
              )}
              <div className="text-3xl sm:text-4xl font-black text-brand-700 tracking-tight leading-none">
                {priceText}
              </div>
            </div>

            {/* Divider */}
            {points > 0 && <div className="hidden sm:block w-px h-10 bg-brand-200" />}

            {/* Points (PV) Section */}
            {points > 0 && (
              <div className="flex flex-col justify-center">
    
                <div className="flex items-center gap-1.5 text-2xl sm:text-3xl font-black text-brand-700 tracking-tight leading-none">
                  <Sparkles className="h-5 w-5 sm:h-6 sm:w-6 text-brand-500" />
                  {formatNumber(points)} <span className="text-base font-bold text-brand-600">PV</span>
                </div>
              </div>
            )}
            
          </div>

          <Card className="mt-8 border-0 shadow-elevate-1 ring-1 ring-surface-200/60 rounded-2xl bg-white/50 backdrop-blur-xl">
            <CardBody className="p-5 sm:p-6 space-y-6">
              
              {/* Dynamic Options Picker */}
              {Object.keys(availableOptions).map((optionKey) => (
                <div key={optionKey}>
                  <div className="text-xs font-bold text-surface-500 mb-3 uppercase tracking-wider">{optionKey}</div>
                  <div className="flex flex-wrap gap-2.5">
                    {Array.from(availableOptions[optionKey]).map((val) => {
                      const isSelected = selectedOptions[optionKey] === val;
                      return (
                        <button
                          key={val}
                          onClick={() => setSelectedOptions(prev => ({...prev, [optionKey]: val}))}
                          className={clsx(
                            "px-5 py-2.5 rounded-xl text-sm font-bold transition-all",
                            isSelected ? "bg-surface-900 text-white shadow-md ring-2 ring-offset-2 ring-surface-900 scale-[1.02]" : "bg-white text-surface-700 hover:bg-surface-100 border border-surface-200 shadow-sm"
                          )}
                        >
                          {val}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}

              {/* Quantity & Actions */}
              <div className="flex flex-col sm:flex-row items-center gap-4 pt-2">
                <div className="flex items-center gap-2 bg-surface-50 border border-surface-200 rounded-xl p-1.5 w-full sm:w-auto justify-center">
                  <button onClick={() => setQty(n => Math.max(1, n - 1))} className="w-10 h-10 rounded-lg flex items-center justify-center hover:bg-white hover:shadow-sm transition-all text-surface-600"><Minus className="h-5 w-5" /></button>
                  <div className="w-10 text-center font-bold text-lg text-surface-900">{qty}</div>
                  <button onClick={() => setQty(n => Math.min(999, n + 1))} className="w-10 h-10 rounded-lg flex items-center justify-center hover:bg-white hover:shadow-sm transition-all text-surface-600"><Plus className="h-5 w-5" /></button>
                </div>
                
                <Button size="lg" variant="outline" className="flex-1 rounded-xl h-14 w-full sm:w-auto font-bold border-surface-300" leftIcon={<ShoppingCart className="h-5 w-5" />} onClick={() => handleAction(false)}>
                  Add to cart
                </Button>
              </div>

              <Button size="lg" className="w-full rounded-xl h-14 text-lg font-extrabold shadow-brand-500/25 transition-transform hover:-translate-y-0.5" leftIcon={<Zap className="h-5 w-5 fill-white" />} onClick={() => handleAction(true)}>
                Buy it now
              </Button>
            </CardBody>
          </Card>

          <div className="mt-10 pt-8 border-t border-surface-200 text-surface-700 leading-relaxed whitespace-pre-wrap">
            <h3 className="font-bold text-surface-900 mb-4 text-lg">Product Description</h3>
            {data.description}
          </div>
        </div>
      </div>
    </div>
  );
}